import React, {
    useState,
    useEffect,
} from 'react'
import type { ComponentType } from 'react'
import { useTranslation } from 'react-i18next'
import {
    readCompanies,
    Company,
} from './helpers'
import { withConnectWrapper } from '../../../validator'

type AnyProps = Record<string, unknown>
import Modal from '../../../components/shared/modal'
import CompanyForm from './forms/company-form'
import CompaniesTable from './CompaniesTable'
import TableCard from './TableCard'

interface SuperAdminUsersCompanyProps {
    companies: Company[]
}

/**
 * Super-admin companies management page.
 * Displays a list of companies and allows creating / editing them via a modal.
 */
function SuperAdminUsersCompany({ companies }: SuperAdminUsersCompanyProps) {
    const { t } = useTranslation()

    const [modalOpen, setModalOpen] = useState<boolean>(false)
    const [editedCompany, setEditedCompany] = useState<Partial<Company> | null>(null)

    useEffect(() => {
        if (companies?.length === 0) {
            readCompanies()
        }
    }, [companies?.length])

    /**
     * Set edited company form
     *
     * @param {string} key
     * @param {string} value
     * @return void
     */
    const setEditedCompanyForm = (key: keyof Company, value: string): void => {
        setEditedCompany((prev) => ({
            ...prev,
            [key]: value,
        }))
    }

    /**
     * Edit company and open modal
     *
     * @param {Partial<Company>|null} company
     * @param {boolean} modalOpen
     */
    const editCompany = (company: Partial<Company> | null, open = true): void => {
        setModalOpen(open)
        setEditedCompany(company || {
            uid: undefined,
            name: '',
        })
    }

    return (
        <div className="container">
            <TableCard
                toggleModal={() => editCompany(null)}
                title={t('company.companies')}
                buttonText={t('company.addCompany')}
            >
                <CompaniesTable editCompany={editCompany}/>
            </TableCard>

            {modalOpen && editedCompany && (
                <Modal
                    description={editedCompany.uid ? t('company.modal.edit') : t('company.modal.add')}
                    handleHideModal={() => editCompany(null, false)}
                    content={() => (
                        <CompanyForm
                            editedCompany={editedCompany as Company}
                            setEditedCompanyForm={setEditedCompanyForm}
                            successSaveFunction={() => editCompany(null, false)}
                        />
                    )}
                />
            )}
        </div>
    )
}

export default withConnectWrapper((state: { superAdmin: { companies: Company[] } }) => ({
    companies: state.superAdmin.companies,
}))(SuperAdminUsersCompany as unknown as ComponentType<AnyProps>)
